<script setup lang="ts">
import LOGO_TRANSPARENT from "~/assets/Logo - Transparent.png";
import CustomLink from "./CustomLink.vue";
import Button from "~/components/atoms/Button.vue";
import SelectionsCoachMark from "~/components/molecules/SelectionsCoachMark.vue";
import { useArtBrowsingNudge } from "~/composables/useArtBrowsingNudge";
import { onClickOutside } from "@vueuse/core";

const route = useRoute();
const { localSavedItemsCount } = useAppState();
const { isLoggedIn, profile, signout } = useAuth();
const { isArtist } = useArtistAccess();
const { toggleCart, cartItemCount, isCartBumping } = useCart();
const config = useRuntimeConfig();

const { shouldShowNudge, dismissNudge, remainingSeconds } = useArtBrowsingNudge();

const NUDGE_RADIUS = 9;
const NUDGE_CIRCUMFERENCE = 2 * Math.PI * NUDGE_RADIUS;
const mobileTimerDashoffset = computed(() => {
  const progress = Math.max(0, Math.min(1, remainingSeconds.value / 10));
  return NUDGE_CIRCUMFERENCE * (1 - progress);
});

const avatarUrl = computed(() => {
  if (profile.value?.photo) {
    if (profile.value.photo.startsWith("http://") || profile.value.photo.startsWith("https://")) {
      return profile.value.photo;
    }
    const cdnUrl = config.public.imageCdnUrl || "https://duq6bmhfzq92t.cloudfront.net/public-static";
    let filename = profile.value.photo;
    if (filename.includes("/")) {
      filename = filename.split("/").pop() || "";
    }
    return `${cdnUrl}/profile-images/${filename}`;
  }
  return useSupabaseUser().value?.user_metadata?.avatar_url || null;
});

const isOpen = ref(false);
const isUserMenuOpen = ref(false);
const isSignoutModalOpen = ref(false);

const desktopUserMenuRef = ref<HTMLElement | null>(null);
const mobileUserMenuRef = ref<HTMLElement | null>(null);

function toggleUserMenu() {
  isUserMenuOpen.value = !isUserMenuOpen.value;
}

const toggle = () => {
  isOpen.value = !isOpen.value;
  if (isOpen.value) document.body.style.overflow = "hidden";
  else document.body.style.overflow = "";
};
const close = () => {
  isOpen.value = false;
  document.body.style.overflow = "";
};

// Derive display name (first name only)
const displayName = computed(() => {
  if (!profile.value?.name) return null;
  return profile.value.name.split(" ")[0];
});


function handleSignout() {
  isUserMenuOpen.value = false;
  isOpen.value = false; // close mobile menu if open
  isSignoutModalOpen.value = true;
}

function handleMobileNudgeClick() {
  dismissNudge();
  navigateTo("/selections?tab=community");
}

async function confirmSignout() {
  isSignoutModalOpen.value = false;
  await signout();
  navigateTo("/");
}

// Close user menu on outside click safely
if (import.meta.client) {
  onClickOutside(desktopUserMenuRef, (e) => {
    const target = e.target as Node | null;
    if (target && mobileUserMenuRef.value?.contains(target)) {
      return;
    }
    isUserMenuOpen.value = false;
  });

  onClickOutside(mobileUserMenuRef, (e) => {
    const target = e.target as Node | null;
    if (target && desktopUserMenuRef.value?.contains(target)) {
      return;
    }
    isUserMenuOpen.value = false;
  });
}

watch(
  () => route.fullPath,
  () => {
    isUserMenuOpen.value = false;
    isOpen.value = false;
  }
);
</script>


<template>
  <nav class="sticky top-0 z-[105] flex w-full flex-[0_0_auto] bg-[var(--color-primary,#fff)] bg-primary py-4" style="z-index: 105;">
    <div class="main-container flex w-full">
      <!-- Logo -->
      <NuxtLink to="/" class="flex-none" @click="close">
        <img class="h-8 lg:h-12" :src="LOGO_TRANSPARENT" alt="Quiet Art Movement" />
      </NuxtLink>

      <!-- Desktop links -->
      <div v-if="route.path !== '/artists/complete-profile' && route.path !== '/artists/reapply'" class="hidden w-full justify-end gap-12 sm:flex lg:flex items-center">
        <CustomLink
          class="text-xs font-medium md:text-base"
          to="/artists"
          text="The Artists"
          disable-icon
          :selected="route.path.startsWith('/artists')"
        />
        <CustomLink
          class="text-xs font-medium md:text-base"
          to="/artworks"
          text="The Art"
          disable-icon
          :selected="route.path.startsWith('/artworks')"
        />

        <!-- The Selections with Nudge Underline & Dark Popover -->
        <div class="relative flex items-center justify-center selections-nav-item">
          <NuxtLink
            :to="shouldShowNudge ? '/selections?tab=community' : '/selections'"
            class="text-xs font-medium md:text-base inline-flex items-center justify-center gap-2 text-on-primary-heading relative group no-underline"
            @click="dismissNudge"
          >
            <span>The Selections</span>
            <span
              v-if="shouldShowNudge"
              class="selections-nav-item__underline"
              aria-hidden="true"
            />
            <span
              v-else
              class="absolute -bottom-1.5 left-0 h-[2px] bg-on-primary-heading transition-[width] duration-[600ms] ease"
              :style="{ width: route.path.startsWith('/selections') ? '100%' : '0%' }"
            />
          </NuxtLink>

          <SelectionsCoachMark
            :show="shouldShowNudge"
            :seconds-left="remainingSeconds"
            :duration="10"
            @dismiss="dismissNudge"
            @accept="dismissNudge"
          />
        </div>

        <CustomLink
          class="text-xs font-medium md:text-base"
          to="/constellations"
          text="The Constellations"
          disable-icon
          :selected="route.path.startsWith('/constellations')"
        /> 
        <CustomLink
          class="text-xs font-medium md:text-base"
          to="/the-movement"
          text="The Movement"
          disable-icon
          :selected="route.path.startsWith('/the-movement')"
        />
        <CustomLink
          v-if="false"
          class="text-xs font-medium md:text-base"
          to="/artist-dashboard"
          text="My Dashboard"
          disable-icon
          :selected="route.path === '/artist-dashboard'"
        />

        <!-- Cart Icon -->
        <button
          v-if="isLoggedIn"
          class="relative flex items-center justify-center p-2 text-on-primary-heading hover:opacity-80 cursor-pointer bg-transparent border-none rounded-full cart-nav-btn"
          :class="{ 'cart-pulse-active': isCartBumping }"
          @click="toggleCart"
          aria-label="Toggle Cart"
        >
          <span v-if="isCartBumping" class="cart-burst-ring" aria-hidden="true" />
          <Icon name="lucide:shopping-bag" class="w-5 h-5 transition-transform" :class="{ 'cart-icon-bump': isCartBumping }" />
          <span
            v-if="cartItemCount > 0"
            class="absolute top-0.5 right-0 flex h-4 w-4 items-center justify-center rounded-full bg-button-primary text-[10px] font-bold text-white transition-transform"
            :class="{ 'cart-badge-bump': isCartBumping }"
          >
            {{ cartItemCount }}
          </span>
        </button>

        <!-- Auth — user menu (always visible, always person icon) -->
        <div ref="desktopUserMenuRef" class="nav-user-menu relative">
          <button
            type="button"
            class="nav-avatar"
            :aria-label="isLoggedIn ? `User menu for ${displayName || 'User'}` : 'User menu'"
            :aria-expanded="isUserMenuOpen"
            @click.stop="toggleUserMenu"
          >
            <ClientOnly>
              <img
                v-if="avatarUrl"
                :src="avatarUrl"
                class="w-full h-full object-cover rounded-full pointer-events-none"
                alt="Profile picture"
                referrerpolicy="no-referrer"
              />
              <Icon v-else name="lucide:user" class="w-4 h-4 text-[var(--color-primary)] pointer-events-none" />
              <template #fallback>
                <Icon name="lucide:user" class="w-4 h-4 text-[var(--color-primary)] pointer-events-none" />
              </template>
            </ClientOnly>
          </button>

          <Transition name="nav-dropdown">
            <div v-if="isUserMenuOpen" class="nav-dropdown">
              <template v-if="isLoggedIn">
                <NuxtLink v-if="displayName" to="/profile" class="nav-dropdown__name nav-dropdown__name--link" @click="isUserMenuOpen = false">
                  {{ displayName }}
                </NuxtLink>
                <div v-if="displayName" class="nav-dropdown__divider" />
                <NuxtLink to="/addresses" class="nav-dropdown__item" @click="isUserMenuOpen = false">
                  <Icon name="lucide:book-user" class="nav-dropdown__item-icon" />
                  Address Book
                </NuxtLink>
                <NuxtLink to="/orders" class="nav-dropdown__item" @click="isUserMenuOpen = false">
                  <Icon name="lucide:package" class="nav-dropdown__item-icon" />
                  Order History
                </NuxtLink>
                <NuxtLink to="/selections/manage" class="nav-dropdown__item" @click="isUserMenuOpen = false">
                  <Icon name="lucide:heart" class="nav-dropdown__item-icon" />
                  My Selections
                </NuxtLink>
                <NuxtLink v-if="false" to="/artist-dashboard" class="nav-dropdown__item" @click="isUserMenuOpen = false">
                  <Icon name="lucide:layout-dashboard" class="nav-dropdown__item-icon" />
                  My Dashboard
                </NuxtLink>
                <button type="button" class="nav-dropdown__item" @click="handleSignout">
                  <Icon name="lucide:log-out" class="nav-dropdown__item-icon" />
                  Sign Out
                </button>
              </template>
              <template v-else>
                <NuxtLink :to="{ path: '/signin', query: !['/signin', '/signup'].includes(route.path) ? { redirect: route.fullPath } : undefined }" class="nav-dropdown__item" @click="isUserMenuOpen = false">
                  <Icon name="lucide:log-in" class="nav-dropdown__item-icon" />
                  Sign In
                </NuxtLink>
              </template>
            </div>
          </Transition>
        </div>
      </div>


      <!-- Mobile Actions (Cart + Hamburger) -->
      <div v-if="route.path !== '/artists/complete-profile' && route.path !== '/artists/reapply'" class="flex w-full items-center justify-end sm:hidden gap-4">
        <!-- Cart Icon (Mobile) -->
        <button
          v-if="isLoggedIn"
          type="button"
          class="relative flex items-center justify-center p-1 mr-1 text-on-primary-heading bg-transparent border-none cursor-pointer rounded-full cart-nav-btn"
          :class="{ 'cart-pulse-active': isCartBumping }"
          @click="toggleCart"
          aria-label="Toggle Cart"
        >
          <span v-if="isCartBumping" class="cart-burst-ring" aria-hidden="true" />
          <Icon name="lucide:shopping-bag" class="w-5 h-5 transition-transform" :class="{ 'cart-icon-bump': isCartBumping }" />
          <span
            v-if="cartItemCount > 0"
            class="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-button-primary text-[10px] font-bold text-white transition-transform"
            :class="{ 'cart-badge-bump': isCartBumping }"
          >
            {{ cartItemCount }}
          </span>
        </button>

        <!-- Auth — user menu (Mobile) -->
        <div ref="mobileUserMenuRef" class="nav-user-menu relative">
          <button
            type="button"
            class="nav-avatar"
            :aria-label="isLoggedIn ? `User menu for ${displayName || 'User'}` : 'User menu'"
            :aria-expanded="isUserMenuOpen"
            @click.stop="toggleUserMenu"
          >
            <ClientOnly>
              <img
                v-if="avatarUrl"
                :src="avatarUrl"
                class="w-full h-full object-cover rounded-full pointer-events-none"
                alt="Profile picture"
                referrerpolicy="no-referrer"
              />
              <Icon v-else name="lucide:user" class="w-4 h-4 text-[var(--color-primary)] pointer-events-none" />
              <template #fallback>
                <Icon name="lucide:user" class="w-4 h-4 text-[var(--color-primary)] pointer-events-none" />
              </template>
            </ClientOnly>
          </button>

          <Transition name="nav-dropdown">
            <div v-if="isUserMenuOpen" class="nav-dropdown">
              <template v-if="isLoggedIn">
                <NuxtLink v-if="displayName" to="/profile" class="nav-dropdown__name nav-dropdown__name--link" @click="isUserMenuOpen = false">
                  {{ displayName }}
                </NuxtLink>
                <div v-if="displayName" class="nav-dropdown__divider" />
                <NuxtLink to="/addresses" class="nav-dropdown__item" @click="isUserMenuOpen = false">
                  <Icon name="lucide:book-user" class="nav-dropdown__item-icon" />
                  Address Book
                </NuxtLink>
                <NuxtLink to="/orders" class="nav-dropdown__item" @click="isUserMenuOpen = false">
                  <Icon name="lucide:package" class="nav-dropdown__item-icon" />
                  Order History
                </NuxtLink>
                <NuxtLink to="/selections/manage" class="nav-dropdown__item" @click="isUserMenuOpen = false">
                  <Icon name="lucide:heart" class="nav-dropdown__item-icon" />
                  My Selections
                </NuxtLink>
                <NuxtLink v-if="false" to="/artist-dashboard" class="nav-dropdown__item" @click="isUserMenuOpen = false">
                  <Icon name="lucide:layout-dashboard" class="nav-dropdown__item-icon" />
                  My Dashboard
                </NuxtLink>
                <button type="button" class="nav-dropdown__item" @click="handleSignout">
                  <Icon name="lucide:log-out" class="nav-dropdown__item-icon" />
                  Sign Out
                </button>
              </template>
              <template v-else>
                <NuxtLink :to="{ path: '/signin', query: !['/signin', '/signup'].includes(route.path) ? { redirect: route.fullPath } : undefined }" class="nav-dropdown__item" @click="isUserMenuOpen = false">
                  <Icon name="lucide:log-in" class="nav-dropdown__item-icon" />
                  Sign In
                </NuxtLink>
              </template>
            </div>
          </Transition>
        </div>

        <!-- Hamburger button (mobile only) -->
        <div class="relative">
          <button
            class="hamburger flex cursor-pointer flex-col gap-1.25 border-none bg-transparent p-1 ml-1"
            :class="{ open: isOpen }"
            aria-label="Toggle menu"
            @click="toggle"
          >
            <span
              class="block h-0.5 w-5 origin-center bg-current transition duration-300 ease-in-out"
            />
            <span
              class="block h-0.5 w-5 origin-center bg-current transition duration-300 ease-in-out"
            />
            <span
              class="block h-0.5 w-5 origin-center bg-current transition duration-300 ease-in-out"
            />
          </button>
        </div>
      </div>
    </div>

    <!-- Mobile Floating Bottom Toast Nudge (Option 1) -->
    <Transition
      enter-active-class="transition duration-300 ease-out transform"
      enter-from-class="opacity-0 translate-y-6 scale-95"
      enter-to-class="opacity-100 translate-y-0 scale-100"
      leave-active-class="transition duration-200 ease-in transform"
      leave-from-class="opacity-100 translate-y-0 scale-100"
      leave-to-class="opacity-0 translate-y-6 scale-95"
    >
      <div
        v-if="shouldShowNudge && !isOpen"
        class="fixed bottom-5 inset-x-4 max-w-sm mx-auto z-50 sm:hidden cursor-pointer"
        @click="handleMobileNudgeClick"
      >
        <div
          class="relative bg-[#1a1a1a] text-white rounded-[12px] p-4 pr-12 shadow-[0_12px_32px_rgba(0,0,0,0.35)] border border-white/10"
        >
          <!-- Close Button with Gray Ring -->
          <button
            type="button"
            @click.stop.prevent="dismissNudge"
            class="absolute top-3.5 right-3.5 w-[22px] h-[22px] rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-all cursor-pointer bg-transparent border-none flex items-center justify-center p-0 font-sans"
            aria-label="Dismiss notification"
            title="Dismiss"
          >
            <svg class="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 22 22" aria-hidden="true">
              <circle
                cx="11"
                cy="11"
                :r="NUDGE_RADIUS"
                fill="none"
                stroke="rgba(255,255,255,0.15)"
                stroke-width="1.5"
              />
              <circle
                cx="11"
                cy="11"
                :r="NUDGE_RADIUS"
                fill="none"
                stroke="rgba(255,255,255,0.5)"
                stroke-width="1.5"
                stroke-linecap="round"
                :stroke-dasharray="NUDGE_CIRCUMFERENCE"
                :stroke-dashoffset="mobileTimerDashoffset"
                class="transition-[stroke-dashoffset] duration-1000 ease-linear"
                transform="rotate(-90 11 11)"
              />
            </svg>
            <Icon name="lucide:x" class="relative z-10 size-3 text-white/75" />
          </button>

          <!-- Title & Description -->
          <p class="text-[14px] font-medium leading-snug text-white m-0 mb-1">
            Looking for some inspiration?
          </p>
          <p class="text-[12px] text-white/70 leading-relaxed m-0">
            See what others have been selecting!
          </p>
        </div>
      </div>
    </Transition>
  </nav>

  <!-- Full-screen mobile menu -->
  <Transition name="fade">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-100 flex flex-col items-center overflow-y-auto bg-primary sm:hidden py-24"
    >
      <ul
        class="m-0 main-container flex list-none flex-col gap-8 p-0 text-on-primary-heading"
      >
        <li>
          <NuxtLink
            to="/artists"
            :class="[
              'text-2xl font-medium no-underline relative group',
              route.path.startsWith('/artists') ? 'opacity-100' : 'opacity-70',
            ]"
            @click="close"
          >
            <span>The Artists</span>
            <span
              class="absolute -bottom-1.5 left-0 h-[2px] bg-on-primary-heading transition-[width] duration-[600ms] ease"
              :style="{ width: route.path.startsWith('/artists') ? '100%' : '0%' }"
            ></span>
          </NuxtLink>
        </li>
        <li>
          <NuxtLink
            to="/artworks"
            :class="[
              'text-2xl font-medium no-underline relative group',
              route.path.startsWith('/artworks') ? 'opacity-100' : 'opacity-70',
            ]"
            @click="close"
          >
            <span>The Art</span>
            <span
              class="absolute -bottom-1.5 left-0 h-[2px] bg-on-primary-heading transition-[width] duration-[600ms] ease"
              :style="{ width: route.path.startsWith('/artworks') ? '100%' : '0%' }"
            ></span>
          </NuxtLink>
        </li>
        <!-- Mobile Selections link -->
        <li class="relative">
          <NuxtLink
            :to="shouldShowNudge ? '/selections?tab=community' : '/selections'"
            :class="[
              'text-2xl font-medium no-underline relative group z-10 inline-flex items-center gap-2',
              route.path.startsWith('/selections') ? 'opacity-100' : 'opacity-70',
            ]"
            @click="() => { dismissNudge(); close(); }"
          >
            <span>The Selections</span>
            <span
              v-if="shouldShowNudge"
              class="selections-nav-item__underline"
              aria-hidden="true"
            />
            <span
              v-else
              class="absolute -bottom-1.5 left-0 h-[2px] bg-on-primary-heading transition-[width] duration-[600ms] ease"
              :style="{ width: route.path.startsWith('/selections') ? '100%' : '0%' }"
            ></span>
          </NuxtLink>
        </li>
        <li>
          <NuxtLink
            to="/constellations"
            :class="[
              'text-2xl font-medium no-underline relative group',
              route.path.startsWith('/constellations') ? 'opacity-100' : 'opacity-70',
            ]"
            @click="close"
          >
            <span>The Constellations</span>
            <span
              class="absolute -bottom-1.5 left-0 h-[2px] bg-on-primary-heading transition-[width] duration-[600ms] ease"
              :style="{ width: route.path.startsWith('/constellations') ? '100%' : '0%' }"
            ></span>
          </NuxtLink>
        </li>
        <li>
          <NuxtLink
            to="/the-movement"
            :class="[
              'text-2xl font-medium no-underline relative group',
              route.path.startsWith('/the-movement') ? 'opacity-100' : 'opacity-70',
            ]"
            @click="close"
          >
            <span>The Movement</span>
            <span
              class="absolute -bottom-1.5 left-0 h-[2px] bg-on-primary-heading transition-[width] duration-[600ms] ease"
              :style="{ width: route.path.startsWith('/the-movement') ? '100%' : '0%' }"
            ></span>
          </NuxtLink>
        </li>

        <!-- Mobile auth -->
        <template v-if="!isLoggedIn">
          <li>
            <NuxtLink
              :to="{ path: '/signin', query: !['/signin', '/signup'].includes(route.path) ? { redirect: route.fullPath } : undefined }"
              class="text-2xl font-medium no-underline opacity-70"
              @click="close"
            >
              Sign In
            </NuxtLink>
          </li>
        </template>
        <template v-else>
          <li>
            <NuxtLink
              to="/profile"
              :class="[
                'text-2xl font-medium no-underline relative group',
                route.path === '/profile' ? 'opacity-100' : 'opacity-70',
              ]"
              @click="close"
            >
              <span>Profile</span>
              <span
                class="absolute -bottom-1.5 left-0 h-[2px] bg-on-primary-heading transition-[width] duration-[600ms] ease"
                :style="{ width: route.path === '/profile' ? '100%' : '0%' }"
              ></span>
            </NuxtLink>
          </li>

          <li v-if="false">
            <NuxtLink
              to="/artist-dashboard"
              :class="[
                'text-2xl font-medium no-underline relative group',
                route.path === '/artist-dashboard' ? 'opacity-100' : 'opacity-70',
              ]"
              @click="close"
            >
              <span>My Dashboard</span>
              <span
                class="absolute -bottom-1.5 left-0 h-[2px] bg-on-primary-heading transition-[width] duration-[600ms] ease"
                :style="{ width: route.path === '/artist-dashboard' ? '100%' : '0%' }"
              ></span>
            </NuxtLink>
          </li>
          <li>
            <button
              class="text-2xl font-medium no-underline opacity-70 bg-transparent border-none cursor-pointer p-0 text-current"
              @click="handleSignout(); close();"
            >
              Sign Out
            </button>
          </li>
        </template>
      </ul>
    </div>
  </Transition>

  <!-- Signout Confirmation Modal -->
  <UModal
    v-model:open="isSignoutModalOpen"
    title="Confirm Sign Out"
    close-icon="lucide:x"
    :ui="{
      content: 'rounded-[6px] shadow-none border border-on-primary-heading',
      close: 'cursor-pointer',
    }"
  >
    <template #body>
      <div class="flex flex-col gap-6">
        <p class="text-base text-on-primary-body">
          Are you sure you want to sign out?
        </p>
        <div class="flex items-center justify-end gap-3">
          <Button variant="secondary" @click="isSignoutModalOpen = false"> Cancel </Button>
          <Button variant="primary" @click="confirmSignout"> Sign Out </Button>
        </div>
      </div>
    </template>
  </UModal>
</template>

<style scoped>
.hamburger.open span:nth-child(1) {
  transform: translateY(7px) rotate(45deg);
}
.hamburger.open span:nth-child(2) {
  opacity: 0;
}
.hamburger.open span:nth-child(3) {
  transform: translateY(-7px) rotate(-45deg);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* ── Auth — Sign In link ─────────────────────────── */
.nav-auth-signin {
  color: var(--color-on-primary-body);
  text-decoration: none;
  transition: color 0.15s;
  white-space: nowrap;
  underline-offset: 4px;
  text-underline-offset: 4px;
  text-decoration: underline;
  text-decoration-color: transparent;
}
.nav-auth-signin:hover {
  color: var(--color-on-primary-heading);
  text-decoration-color: var(--color-on-primary-heading);
}

/* ── Auth — Avatar button ────────────────────────── */
.nav-user-menu {
  position: relative;
}
.nav-avatar {
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: var(--color-on-primary-heading);
  border: 1px solid var(--color-on-primary-heading);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
  padding: 0;
}
.nav-avatar__initials {
  font-size: 0.625rem;
  font-weight: 600;
  color: var(--color-primary);
  letter-spacing: 0.04em;
  line-height: 1;
}

/* ── Auth — Dropdown ─────────────────────────────── */
.nav-dropdown {
  position: absolute;
  top: calc(100% + 0.625rem);
  right: 0;
  min-width: 168px;
  background: var(--color-primary);
  border: 1px solid var(--color-on-primary-border);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  padding: 0.375rem;
  border-radius: 6px;
  z-index: 200;
}
.nav-dropdown__name {
  display: block;
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--color-on-primary-heading);
  padding: 0.375rem 0.625rem 0.5rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-decoration: none;
}
.nav-dropdown__name--link {
  cursor: pointer;
  transition: background 0.12s;
}
.nav-dropdown__name--link:hover {
  background: var(--color-button-secondary);
  color: var(--color-on-primary-heading);
}
.nav-dropdown__divider {
  height: 1px;
  background: var(--color-on-primary-border);
  margin: 0.25rem 0;
}
.nav-dropdown__item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.5rem 0.625rem;
  background: none;
  border: none;
  font-size: 0.8125rem;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.12s;
  text-align: left;
  color: var(--color-on-primary-body);
}
.nav-dropdown__item:hover {
  background: var(--color-button-secondary);
  color: var(--color-on-primary-heading);
}
.nav-dropdown__item-icon {
  width: 0.875rem;
  height: 0.875rem;
  flex-shrink: 0;
  opacity: 0.7;
}

/* Dropdown transition */
.nav-dropdown-enter-active,
.nav-dropdown-leave-active {
  transition: opacity 0.15s, transform 0.15s;
}
.nav-dropdown-enter-from,
.nav-dropdown-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* ── Selections Nudge Styles ─────────────────────── */
.selections-nav-item {
  position: relative;
  display: inline-flex;
  align-items: center;
  z-index: 120;
}

.selections-nav-item__underline {
  position: absolute;
  left: 0;
  right: 0;
  bottom: -6px;
  height: 2px;
  background: var(--color-on-primary-heading, #0A0A0A);
  animation: nudge-underline-pulse 1.8s ease-in-out infinite;
}

@keyframes nudge-underline-pulse {
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
}

/* ── Cart Pulse & Bump Animation (matching buyback ring pulse) ── */
.cart-nav-btn {
  transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.cart-pulse-active {
  animation: cart-ring-pulse 1.2s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

.cart-burst-ring {
  position: absolute;
  inset: -3px;
  border-radius: 9999px;
  pointer-events: none;
  border: 2px solid var(--color-on-primary-heading, #0A0A0A);
  animation: cart-ring-expand 1.2s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

.cart-icon-bump {
  animation: cart-bounce 0.65s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
}

.cart-badge-bump {
  animation: badge-pop 0.65s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
}

@keyframes cart-ring-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(10, 10, 10, 0.4);
  }
  100% {
    box-shadow: 0 0 0 16px rgba(10, 10, 10, 0);
  }
}

@keyframes cart-ring-expand {
  0% {
    transform: scale(0.85);
    opacity: 0.9;
    border-width: 2.5px;
  }
  50% {
    opacity: 0.5;
    border-width: 1.5px;
  }
  100% {
    transform: scale(1.7);
    opacity: 0;
    border-width: 0.5px;
  }
}

@keyframes cart-bounce {
  0% {
    transform: scale(1);
  }
  25% {
    transform: scale(1.35) rotate(-10deg);
  }
  50% {
    transform: scale(0.9) rotate(5deg);
  }
  75% {
    transform: scale(1.1) rotate(-2deg);
  }
  100% {
    transform: scale(1) rotate(0deg);
  }
}

@keyframes badge-pop {
  0% {
    transform: scale(0.4);
  }
  45% {
    transform: scale(1.5);
  }
  75% {
    transform: scale(0.9);
  }
  100% {
    transform: scale(1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .cart-pulse-active,
  .cart-burst-ring,
  .cart-icon-bump,
  .cart-badge-bump {
    animation: none !important;
  }
}
</style>
